import { TFunction } from 'i18next'
import { useEffect, useMemo, useState } from 'react'

import { Nullable } from '@types'

import { Company, ProfileType } from '@modules/search/types'
import { useQueryParams } from '@shared/hooks/useQueryParams'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { ASSETS_OVERVIEW_ORDERED_TABS, ASSETS_OVERVIEW_TAB_LABELS } from '../constants'

interface UseAssetTabsProps<T, R> {
  apiKey: string
  t: TFunction
  apiFn: (_: T) => Promise<R & { facets: Record<ProfileType, number> }>
  companyId?: Company['id']
  codes?: number[]
}

const useAssetTabs = <T, R>(props: UseAssetTabsProps<T, R>) => {
  const { apiKey, apiFn, companyId, codes, t } = props
  const { get, setSilently } = useQueryParams()
  const initialTab = get('tab') as ProfileType

  const [activeTab, setActiveTab] = useState<Nullable<ProfileType>>(initialTab ?? null)

  const { data: { facets } = {}, isLoading } = useQuery({
    queryKey: [apiKey, companyId, codes],
    queryFn: () =>
      apiFn({
        companyId,
        from: 0,
        size: 0,
        types: ASSETS_OVERVIEW_ORDERED_TABS.map(type => ({ type })),
        codes,
      } as T),

    enabled: !!companyId,
    placeholderData: keepPreviousData,
  })

  const tabs = useMemo(() => {
    if (!facets) {
      return []
    }

    const list = ASSETS_OVERVIEW_ORDERED_TABS.filter(i => facets[i])

    return list.map(i => ({
      key: i,
      label: t(ASSETS_OVERVIEW_TAB_LABELS[i as keyof typeof ASSETS_OVERVIEW_TAB_LABELS], { count: facets[i] }),
    }))
  }, [facets, t])

  useEffect(() => {
    if (!tabs?.length) {
      return
    }

    setActiveTab(tabs[0].key)
  }, [tabs])

  useEffect(() => {
    if (activeTab) {
      setSilently('tab', activeTab)
    }
  }, [setSilently, activeTab])

  return {
    tabs,
    activeTab,
    setActiveTab,
    facets,
    isLoading,
  }
}

export default useAssetTabs
